<template>
  <vl-region class="configuration-region">
    <vl-layout>
      <vl-grid>
        <vl-column width="12" width-xs="12">
          <vl-title tag-name="h1">
            {{ $t('configuration.title') }}
          </vl-title>
        </vl-column>
      </vl-grid>
      <vl-tabs :active-tab="data.activeTab" :hash-change="false" @tab-change="onTabChange">
        <vl-tab v-if="isEnabled('entitymodel')" :id="'entitymodel'" :label="$t('configuration.entitymodels.title')">
          <EntityModelSearch v-if="activeHash === 'entitymodel'"></EntityModelSearch>
        </vl-tab>
        <vl-tab
          v-if="isEnabled('deliveryprocessdefinition')"
          :id="'deliveryprocessdefinition'"
          :label="$t('configuration.deliveryProcessDefinitions.title')"
        >
          <DeliveryProcessDefinitionSearch
            v-if="activeHash === 'deliveryprocessdefinition'"
          ></DeliveryProcessDefinitionSearch>
        </vl-tab>
        <vl-tab
          v-if="isEnabled('productprocessdefinition')"
          :id="'productprocessdefinition'"
          :label="$t('configuration.productProcessDefinitions.title')"
        >
          <ProductProcessDefinitionSearch
            v-if="activeHash === 'productprocessdefinition'"
          ></ProductProcessDefinitionSearch>
        </vl-tab>
        <vl-tab v-if="isEnabled('productgroup')" :id="'productgroup'" :label="$t('configuration.productGroups.title')">
          <ProductGroupSearch v-if="activeHash === 'productgroup'"></ProductGroupSearch>
        </vl-tab>
        <vl-tab
          v-if="isEnabled('processtrigger')"
          :id="'processtrigger'"
          :label="$t('configuration.processTriggers.title')"
        >
          <ProcessTriggerSearch v-if="activeHash === 'processtrigger'"></ProcessTriggerSearch>
        </vl-tab>
        <vl-tab v-if="isEnabled('organisation')" :id="'organisation'" :label="$t('configuration.organisations.title')">
          <OrganisationSearch v-if="activeHash === 'organisation'"></OrganisationSearch>
        </vl-tab>
        <vl-tab v-if="isEnabled('systemtable')" :id="'systemtable'" :label="$t('configuration.systemTables.title')">
          <SystemTableSearch v-if="activeHash === 'systemtable'"></SystemTableSearch>
        </vl-tab>
      </vl-tabs>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { FeatureKeys, isFeatureEnabled } from '@/common/config/config';
import { computed, reactive, onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import EntityModelSearch from './entity-model/EntityModelSearch.vue';
import DeliveryProcessDefinitionSearch from './delivery-process-definition/DeliveryProcessDefinitionSearch.vue';
import ProductProcessDefinitionSearch from './product-process-definition/ProductProcessDefinitionSearch.vue';
import ProcessTriggerSearch from './process-trigger/ProcessTriggerSearch.vue';
import OrganisationSearch from './organisation/OrganisationSearch.vue';
import SystemTableSearch from './systemtable/SystemTableSearch.vue';
import ProductGroupSearch from './product-group/ProductGroupSearch.vue';

// vl-tabs numbers the tabs it actually renders, so a tab hidden by its feature flag shifts every
// tab behind it down by one. Both the index and the content must therefore come from this one list.
const tabs: { hash: string; feature: FeatureKeys }[] = [
  { hash: 'entitymodel', feature: 'configurationEntityModelSearch' },
  { hash: 'deliveryprocessdefinition', feature: 'configurationDeliveryProcessDefinitionSearch' },
  { hash: 'productprocessdefinition', feature: 'configurationProductProcessDefinitionSearch' },
  { hash: 'productgroup', feature: 'configurationProductGroupSearch' },
  { hash: 'processtrigger', feature: 'configurationProcessTriggerSearch' },
  { hash: 'organisation', feature: 'configurationOrganisationSearch' },
  { hash: 'systemtable', feature: 'configurationSystemTableSearch' }
];

const enabledTabs = computed(() => tabs.filter((tab) => isFeatureEnabled(tab.feature)));

const isEnabled = (hash: string): boolean => enabledTabs.value.some((tab) => tab.hash === hash);

const data = reactive({
  activeTab: 0
});

// The position vl-tabs reports back, translated to the tab it belongs to.
const activeHash = computed((): string | undefined => enabledTabs.value[data.activeTab]?.hash);

const route = useRoute();
const router = useRouter();

onBeforeMount(() => {
  const hash = route.hash?.replace('#', '')?.toLowerCase();

  data.activeTab = Math.max(
    enabledTabs.value.findIndex((tab) => tab.hash === hash),
    0
  );
});

const onTabChange = (tab) => {
  data.activeTab = tab.index;
  router.push({ query: {}, hash: `#` + tab.id });
};
</script>

<style lang="scss" scoped>
:deep(.vl-tabs) {
  margin-bottom: 0;
}

.configuration-region {
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>
